import React, { useState } from "react";
import { Box, Button, IconButton, InputLabel, MenuItem, Popover, Select, TextField, Tooltip } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useBaseLayoutController } from "../base-layout/context/base-layout-controller";

export interface SelectWithQuickAddOption {
    id: number | string;
    name: string;
}

interface SelectWithQuickAddProps {
    label: string;
    value: number | string | '';
    onChange: (value: any) => void;
    options?: SelectWithQuickAddOption[];
    error?: boolean;
    addLabel: string;
    // Salva o novo item (ex.: chama AdminPortalAppService.addUpdateModalidadeLicitacao({ nomModalidadeLicitacao: nome }))
    onAdd: (nome: string) => Promise<void>;
}

// <Select> com um botão de "+" ao lado que abre um popover simples para
// cadastrar um novo item sem sair do formulário atual (ex.: nova Modalidade
// direto na tela de Licitação). Reaproveitado em todas as famílias PNTP.
export const SelectWithQuickAdd = ({ label, value, onChange, options, error, addLabel, onAdd }: SelectWithQuickAddProps) => {
    const { showMessage } = useBaseLayoutController();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [novoNome, setNovoNome] = useState('');
    const [saving, setSaving] = useState(false);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
        setNovoNome('');
    };

    const handleClose = () => {
        if (saving) return;
        setAnchorEl(null);
    };

    const handleAdd = async () => {
        if (!novoNome.trim()) return;
        try {
            setSaving(true);
            await onAdd(novoNome.trim());
            showMessage({ type: 'success', message: 'Item adicionado com sucesso' });
            setAnchorEl(null);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao adicionar item' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <React.Fragment>
            <InputLabel>{label}</InputLabel>
            <Box display="flex" alignItems="center" gap={0.5}>
                <Select
                    size="small"
                    error={error}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    fullWidth
                >
                    <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                    {options?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                </Select>
                <Tooltip title={addLabel}>
                    <IconButton size="small" color="primary" onClick={handleOpen}>
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
                    <TextField
                        size="small"
                        label={addLabel}
                        value={novoNome}
                        onChange={(e) => setNovoNome(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                        autoFocus
                        fullWidth
                    />
                    <Button variant="contained" size="small" onClick={handleAdd} disabled={saving || !novoNome.trim()}>
                        Adicionar
                    </Button>
                </Box>
            </Popover>
        </React.Fragment>
    );
};
